import type { PropsWithChildren } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text, TruncatedText } from "@similarweb/ui-components/foundations";

import { useToggle } from "app/common/hooks";

const InlineButton = styled.button`
    display: inline-flex;
    background: transparent;
    border: 1px solid transparent;
    cursor: pointer;
    font-weight: 700;
    padding: 0;
    color: ${colorsPalettes.carbon[400]};
    margin-left: 4px;

    &:hover {
        border-bottom-color: ${colorsPalettes.blue[400]};
        color: ${colorsPalettes.blue[400]};
    }
`;

type ExpandButtonProps = PropsWithChildren<{
    onClick: () => void;
}>;

export type ExpandKeySuffix = "read-less" | "read-more";

const ExpandButton = ({ children, onClick, ...props }: ExpandButtonProps) => {
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
    };

    return (
        <InlineButton
            onClick={handleClick}
            {...props}
        >
            <Text fontVariant="subtitle2">{children}</Text>
        </InlineButton>
    );
};

interface IExpandableTextProps extends PropsWithChildren {
    onTrack: (keySuffix: ExpandKeySuffix) => void;
    isExpanded?: boolean;
    onToggle?: () => void;
}

export const ExpandableText = ({
    children,
    onTrack,
    isExpanded: controlledIsExpanded,
    onToggle,
}: IExpandableTextProps) => {
    const [internalIsExpanded, toggleInternalIsExpanded] = useToggle(false);
    const isControlled = controlledIsExpanded !== undefined;
    const isExpanded = isControlled ? controlledIsExpanded : internalIsExpanded;
    const { translate } = useTranslate();
    const expandButtonKeySuffix = isExpanded ? "read-less" : "read-more";
    const handleClick = () => {
        if (isControlled) {
            onToggle?.();
        } else {
            toggleInternalIsExpanded();
        }
        onTrack(expandButtonKeySuffix);
    };

    return (
        <Flex alignItems="flex-end">
            <Flex maxWidth="650px">
                <TruncatedText
                    fontVariant="body2"
                    lineClamp={isExpanded ? 9999 : 2}
                    data-automation="alert-description"
                >
                    {children}
                </TruncatedText>
            </Flex>
            <ExpandButton
                onClick={handleClick}
                data-automation="expand-button"
            >
                {translate(`activation.alerts.cards.${expandButtonKeySuffix}`)}
            </ExpandButton>
        </Flex>
    );
};
